import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flexibilitypay/ui';
import { formatCurrency, formatDateTime } from '@flexibilitypay/shared';

import { useMovementsQuery } from '../hooks/use-movements-query';
import type { MovementItem, MovementsPaginationMeta } from '../api/movements.api';

const PAGE_SIZE = 10;

function useErrorMessage(
  t: (key: string) => string,
  error: Error | null | undefined,
): string | null {
  if (!error) return null;
  const msg = error.message;
  if (msg === 'HTTP_404') return t('movements:errors.notFound');
  if (msg === 'HTTP_500' || msg === 'HTTP_502' || msg === 'HTTP_503') {
    return t('movements:errors.unavailable');
  }
  return t('movements:errors.generic');
}

function isAllowedCurrency(value: string): value is 'ARS' | 'USD' {
  return value === 'ARS' || value === 'USD';
}

function renderImporte(item: MovementItem): string {
  if (!isAllowedCurrency(item.moneda)) {
    return `${item.moneda} ${item.importe.toFixed(2)}`;
  }
  return formatCurrency(String(item.importe), item.moneda);
}

/**
 * (Backfill automático — agregar descripción)
 *
 * @taskId T059b
 * @see functional/Specs/specs_cycle-2.md HU-07-FE
 */
export function MovementsPage() {
  const { idCuenta } = useParams<{ idCuenta?: string }>();
  const { t } = useTranslation(['movements']);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error, isFetching } = useMovementsQuery({
    idCuenta,
    page,
    pageSize: PAGE_SIZE,
  });

  const errorMessage = useErrorMessage(t, error as Error | null);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">
          {t('movements:page.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('movements:page.subtitle')}
        </p>
      </div>

      {!idCuenta ? (
        <ErrorAlert message={t('movements:errors.missingId')} />
      ) : isLoading ? (
        <MovementsTableSkeleton />
      ) : isError ? (
        <ErrorAlert message={errorMessage ?? t('movements:errors.generic')} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title={t('movements:empty.title')}
          description={t('movements:empty.description')}
        />
      ) : (
        <MovementsCard
          items={data.items}
          page={page}
          pageSize={PAGE_SIZE}
          pagination={data.pagination}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => p + 1)}
          isFetching={isFetching}
        />
      )}
    </div>
  );
}

function MovementsCard({
  items,
  page,
  pageSize,
  pagination,
  onPrev,
  onNext,
  isFetching,
}: {
  items: MovementItem[];
  page: number;
  pageSize: number;
  pagination: MovementsPaginationMeta | null;
  onPrev: () => void;
  onNext: () => void;
  isFetching: boolean;
}) {
  const { t } = useTranslation(['movements']);

  const totalPages = pagination?.TotalPages ?? null;
  const totalRecords = pagination?.TotalRecords ?? null;
  const hasMore = totalPages !== null ? page < totalPages : items.length === pageSize;
  const isFirst = page === 1;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1">
          {totalPages !== null && (
            <CardTitle className="text-base">
              {t('movements:pagination.summary', { page, totalPages })}
            </CardTitle>
          )}
          {totalRecords !== null && (
            <CardDescription>
              {t('movements:pagination.totalRecords', { count: totalRecords })}
            </CardDescription>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('movements:columns.fecha')}</TableHead>
              <TableHead>{t('movements:columns.tipo')}</TableHead>
              <TableHead className="text-right">
                {t('movements:columns.monto')}
              </TableHead>
              <TableHead>{t('movements:columns.descripcion')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.idWeb}>
                <TableCell className="font-mono text-xs">
                  {formatDateTime(item.fechaOperacion)}
                </TableCell>
                <TableCell>{item.tipoTransaccion}</TableCell>
                <TableCell className="text-right font-mono">
                  {renderImporte(item)}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {item.descripcion}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                role="button"
                aria-label={t('movements:pagination.previous')}
                aria-disabled={isFirst || isFetching}
                data-disabled={isFirst || isFetching}
                onClick={(e) => {
                  e.preventDefault();
                  if (!isFirst && !isFetching) onPrev();
                }}
                className={isFirst || isFetching ? 'pointer-events-none opacity-50' : ''}
              >
                {t('movements:pagination.previous')}
              </PaginationPrevious>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                role="button"
                aria-label={t('movements:pagination.next')}
                aria-disabled={!hasMore || isFetching}
                data-disabled={!hasMore || isFetching}
                onClick={(e) => {
                  e.preventDefault();
                  if (hasMore && !isFetching) onNext();
                }}
                className={!hasMore || isFetching ? 'pointer-events-none opacity-50' : ''}
              >
                {t('movements:pagination.next')}
              </PaginationNext>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </CardContent>
    </Card>
  );
}

function MovementsTableSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        {Array.from({ length: 5 }).map((_, idx) => (
          <Skeleton key={idx} className="h-10 w-full" />
        ))}
      </CardContent>
    </Card>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card>
      <CardContent className="py-10 text-center">
        <p className="text-base font-medium">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

function ErrorAlert({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
    >
      <AlertCircle className="size-4 mt-0.5 shrink-0" aria-hidden />
      <p className="font-medium">{message}</p>
    </div>
  );
}
